import { useMemo, useRef, useState } from "react";

import { faInbox } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { find } from "lodash-es";

import { AccessRequestReasonCell } from "src/adminPanel/AccessRequestReasonCell";
import { AccessRequestStatusPill } from "src/adminPanel/AccessRequestStatusPill";
import {
  useAccessRequests,
  useAllOrgNames,
  useAllUsers,
  useRequestableRoles,
  useUpdateAccessRequestStatus,
} from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { DisplayDateTime } from "src/base-components/Date";
import { Select } from "src/base-components/Select";
import { Cell, Header, TableComp } from "src/base-components/Table";
import {
  AccessRequestDecisionMethod,
  AccessRequestInDB,
  AccessRequestStatus,
} from "src/clients/taktile-api";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { Input } from "src/design-system/Input";
import { toastActions } from "src/design-system/Toast/utils";
import { useHasRole } from "src/store/AuthStore";
import { errorMessage } from "src/utils/stringUtils";

const PAGE_SIZE = 20;

const LABEL_CLASS = "mb-1.5 block font-medium text-gray-700 text-sm";

const STATUS_FILTERS: (AccessRequestStatus | "all")[] = [
  "all",
  AccessRequestStatus.PENDING,
  AccessRequestStatus.APPROVED,
  AccessRequestStatus.REJECTED,
  AccessRequestStatus.CANCELLED,
];

const STATUS_FILTER_OPTIONS = STATUS_FILTERS.map((value) => ({
  key: value,
  value,
}));

const columnHelper = createColumnHelper<AccessRequestInDB>();

export const AccessApprover: React.FC = () => {
  const updateStatus = useUpdateAccessRequestStatus();
  const allOrgs = useAllOrgNames();
  const allUsers = useAllUsers();
  const requestableRoles = useRequestableRoles();
  const canApprove = useHasRole("access_approver");
  const [statusFilter, setStatusFilter] = useState<AccessRequestStatus | "all">(
    "all",
  );
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const accessRequests = useAccessRequests({
    status: statusFilter === "all" ? undefined : statusFilter,
    organizationId: orgFilter === "all" ? undefined : orgFilter,
    skip: page * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const [reasonByRequestId, setReasonByRequestId] = useState<
    Record<string, string>
  >({});
  const [durationByRequestId, setDurationByRequestId] = useState<
    Record<string, string>
  >({});

  const getOrgName = (orgId: string) =>
    find(allOrgs.data, { id: orgId })?.name ?? orgId;

  const getUserLabel = (userId: string) => {
    const user = find(allUsers.data, { id: userId });
    return user?.full_name || user?.email || user?.username || userId;
  };

  const getRoleDisplayName = (roleName: string) =>
    (requestableRoles.data ?? []).find((role) => role.name === roleName)
      ?.display_name ?? roleName;

  const getDecisionReason = (requestId: string) => {
    const reason = reasonByRequestId[requestId]?.trim();
    return reason === "" || reason === undefined ? undefined : reason;
  };

  const getDurationValue = (request: AccessRequestInDB) =>
    durationByRequestId[request.id] ??
    request.access_duration_days?.toString() ??
    "";

  const getDurationOverride = (request: AccessRequestInDB) => {
    const value = getDurationValue(request).trim();
    if (value === "") {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const handleReasonChange =
    (requestId: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setReasonByRequestId((prev) => ({
        ...prev,
        [requestId]: event.target.value,
      }));

  const handleDurationChange =
    (requestId: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setDurationByRequestId((prev) => ({
        ...prev,
        [requestId]: event.target.value,
      }));

  const handleApprove = (request: AccessRequestInDB) => async () => {
    if (!canApprove) {
      return;
    }
    try {
      await updateStatus.mutateAsync({
        accessRequestId: request.id,
        status: AccessRequestStatus.APPROVED,
        decisionReason: getDecisionReason(request.id),
        accessDurationDays: getDurationOverride(request),
      });
      toastActions.success({ title: "Access request approved" });
    } catch (error) {
      toastActions.failure({
        title: "Couldn't approve access request",
        description: errorMessage(error),
      });
    }
  };

  const handleReject = (request: AccessRequestInDB) => async () => {
    if (!canApprove) {
      return;
    }
    try {
      await updateStatus.mutateAsync({
        accessRequestId: request.id,
        status: AccessRequestStatus.REJECTED,
        decisionReason: getDecisionReason(request.id),
      });
      toastActions.success({ title: "Access request rejected" });
    } catch (error) {
      toastActions.failure({
        title: "Couldn't reject access request",
        description: errorMessage(error),
      });
    }
  };

  const requests = accessRequests.data?.items ?? [];
  const total = accessRequests.data?.total ?? 0;
  const hasMore = accessRequests.data?.has_more ?? false;

  const lastPage = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE) - 1;
  if (accessRequests.data && page > lastPage) {
    setPage(lastPage);
  }

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = page * PAGE_SIZE + requests.length;

  const handleStatusFilterChange = (value: AccessRequestStatus | "all") => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleOrgFilterChange = (value: string) => {
    setOrgFilter(value);
    setPage(0);
  };

  const orgFilterOptions = [
    { key: "all", value: "all" },
    ...[...(allOrgs.data ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((org) => ({ key: org.id, value: org.name })),
  ];

  const renderDecidedBy = (request: AccessRequestInDB) => {
    if (request.status === AccessRequestStatus.PENDING) {
      return "-";
    }
    if (request.decision_method === AccessRequestDecisionMethod.AUTO) {
      return "Auto";
    }
    return request.approver_user_id
      ? getUserLabel(request.approver_user_id)
      : "-";
  };

  const cellPropsRef = useRef<{
    canApprove: boolean;
    isUpdating: boolean;
    getUserLabel: (userId: string) => string;
    getOrgName: (orgId: string) => string;
    getRoleDisplayName: (roleName: string) => string;
    getDurationValue: (request: AccessRequestInDB) => string;
    reasonByRequestId: Record<string, string>;
    handleDurationChange: (
      requestId: string,
    ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleReasonChange: (
      requestId: string,
    ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleApprove: (request: AccessRequestInDB) => () => void;
    handleReject: (request: AccessRequestInDB) => () => void;
    renderDecidedBy: (request: AccessRequestInDB) => React.ReactNode;
  } | null>(null);
  cellPropsRef.current = {
    canApprove,
    isUpdating: updateStatus.isPending,
    getUserLabel,
    getOrgName,
    getRoleDisplayName,
    getDurationValue,
    reasonByRequestId,
    handleDurationChange,
    handleReasonChange,
    handleApprove,
    handleReject,
    renderDecidedBy,
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "requester",
        header: (info) => <Header info={info}>Requester</Header>,
        cell: (info) => (
          <Cell info={info}>
            {cellPropsRef.current?.getUserLabel(
              info.row.original.requester_user_id,
            )}
          </Cell>
        ),
      }),
      columnHelper.display({
        id: "organization",
        header: (info) => <Header info={info}>Organization</Header>,
        cell: (info) => (
          <Cell info={info}>
            {cellPropsRef.current?.getOrgName(
              info.row.original.organization_id,
            )}
          </Cell>
        ),
      }),
      columnHelper.display({
        id: "requestedRole",
        header: (info) => <Header info={info}>Requested role</Header>,
        cell: (info) => (
          <Cell info={info}>
            {cellPropsRef.current?.getRoleDisplayName(
              info.row.original.requested_role,
            )}
          </Cell>
        ),
      }),
      columnHelper.display({
        id: "reason",
        header: (info) => <Header info={info}>Reason</Header>,
        cell: (info) => (
          <AccessRequestReasonCell
            info={info}
            value={info.row.original.reason}
          />
        ),
      }),
      columnHelper.display({
        id: "duration",
        header: (info) => <Header info={info}>Duration (days)</Header>,
        cell: (info) => {
          const props = cellPropsRef.current;
          const request = info.row.original;
          const isPending = request.status === AccessRequestStatus.PENDING;
          return (
            <Cell info={info}>
              {isPending && props ? (
                <div className="w-24">
                  <Input
                    dataLoc={`duration-input-${request.id}`}
                    disabled={!props.canApprove}
                    min={1}
                    placeholder="days"
                    type="number"
                    value={props.getDurationValue(request)}
                    onChange={props.handleDurationChange(request.id)}
                  />
                </div>
              ) : (
                (request.access_duration_days ?? "-")
              )}
            </Cell>
          );
        },
      }),
      columnHelper.display({
        id: "status",
        header: (info) => <Header info={info}>Status</Header>,
        cell: (info) => (
          <Cell info={info}>
            <AccessRequestStatusPill status={info.row.original.status} />
          </Cell>
        ),
      }),
      columnHelper.display({
        id: "created",
        header: (info) => <Header info={info}>Created</Header>,
        cell: (info) => (
          <Cell info={info}>
            <DisplayDateTime value={info.row.original.created_at} />
          </Cell>
        ),
      }),
      columnHelper.display({
        id: "decidedBy",
        header: (info) => <Header info={info}>Decided by</Header>,
        cell: (info) => (
          <Cell info={info}>
            {cellPropsRef.current?.renderDecidedBy(info.row.original)}
          </Cell>
        ),
      }),
      columnHelper.display({
        id: "decisionReason",
        header: (info) => <Header info={info}>Decision reason</Header>,
        cell: (info) => {
          const props = cellPropsRef.current;
          const request = info.row.original;
          const isPending = request.status === AccessRequestStatus.PENDING;
          if (isPending && props) {
            return (
              <Cell info={info}>
                <div className="w-40">
                  <Input
                    dataLoc={`decision-reason-input-${request.id}`}
                    disabled={!props.canApprove}
                    placeholder="Decision reason (optional)"
                    type="text"
                    value={props.reasonByRequestId[request.id] ?? ""}
                    onChange={props.handleReasonChange(request.id)}
                  />
                </div>
              </Cell>
            );
          }
          return (
            <AccessRequestReasonCell
              info={info}
              value={request.decision_reason}
            />
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: (info) => <Header info={info}>Actions</Header>,
        cell: (info) => {
          const props = cellPropsRef.current;
          const request = info.row.original;
          const isPending = request.status === AccessRequestStatus.PENDING;
          if (!isPending || !props) {
            return (
              <Cell info={info}>
                <span className="text-gray-400">-</span>
              </Cell>
            );
          }
          return (
            <Cell info={info}>
              <div className="flex items-center gap-2">
                <Button
                  disabled={!props.canApprove || props.isUpdating}
                  size="sm"
                  variant="primary"
                  onClick={props.handleApprove(request)}
                >
                  Approve
                </Button>
                <Button
                  disabled={!props.canApprove || props.isUpdating}
                  size="sm"
                  variant="warning"
                  onClick={props.handleReject(request)}
                >
                  Reject
                </Button>
              </div>
            </Cell>
          );
        },
      }),
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-8">
          <h1 className="font-bold text-2xl text-gray-900">Approve Access</h1>
          <p className="mt-1 text-gray-500 text-sm">
            Review and decide on access requests for your organizations.
          </p>
        </div>

        {!canApprove && (
          <div className="mb-6">
            <Callout variant="warning">
              You do not have permission to approve access requests.
            </Callout>
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-4">
          <div className="w-full max-w-xs">
            <label className={LABEL_CLASS}>Filter by status</label>
            <Select
              dataLoc="status-filter"
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              fullWidth
              onChange={(value) =>
                handleStatusFilterChange(value as AccessRequestStatus | "all")
              }
            />
          </div>
          <div className="w-full max-w-xs">
            <label className={LABEL_CLASS}>Filter by organization</label>
            <Select
              dataLoc="organization-filter"
              options={orgFilterOptions}
              value={orgFilter}
              fullWidth
              onChange={handleOrgFilterChange}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {accessRequests.isError ? (
            <div className="p-4">
              <Callout variant="error">Error loading access requests</Callout>
            </div>
          ) : (
            <TableComp
              columns={columns}
              data={requests}
              dataLoc="access-requests-table"
              disableVirtualization
              emptyState={
                accessRequests.isLoading ? undefined : (
                  <EmptyState headline="No access requests" icon={faInbox} />
                )
              }
              getRowId={(request) => request.id}
              isLoading={accessRequests.isLoading}
            />
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-gray-500 text-sm">
            {total === 0
              ? "No results"
              : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              disabled={page === 0 || accessRequests.isFetching}
              size="sm"
              variant="secondary"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            >
              Previous
            </Button>
            <Button
              disabled={!hasMore || accessRequests.isFetching}
              size="sm"
              variant="secondary"
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
